/**
 * MBToast — Global toast notification system
 * Uses design tokens. Zustand store for state.
 *
 * Usage:
 *   import { useToast } from '@/stores/toastStore';
 *   const { toast } = useToast();
 *   toast.success('Missao completa!');
 *   toast.error('Falha na conexao');
 *   toast.xp('+50 XP');
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToastStore, type Toast } from '@/stores/toastStore';
import './MBToast.css';

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      onRemove(toast.id);
    }, toast.duration);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [toast.id, toast.duration, onRemove]);

  const iconMap: Record<Toast['type'], string> = {
    success: '✓',
    error: '✕',
    info: 'ℹ',
    xp: '★',
    energy: '⚡',
  };

  return (
    <div
      className={`mb-toast mb-toast--${toast.type}`}
      role="alert"
      aria-live="polite"
    >
      <span className="mb-toast__icon">{iconMap[toast.type]}</span>
      <span className="mb-toast__message">{toast.message}</span>
    </div>
  );
}

export function MBToastContainer() {
  const { t } = useTranslation();
  const { toasts, removeToast } = useToastStore();

  if (toasts.length === 0) return null;

  return (
    <div className="mb-toast-container" aria-label={t('feedback.notifications')}>
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onRemove={removeToast} />
      ))}
    </div>
  );
}
